import { forwardRef, type ReactNode, type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { BaseModalProps, BaseModal } from './BaseModal.js';
import * as styles from './Modal.sty.js';
import type { ModalSizeOptionsType } from './types.js';
import { Button } from '../../buttons/button/Button.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { globalSprinkles } from '../../styles/safe-sprinkles.js';
import { Heading } from '../../typography/heading/Heading.js';

const COMPONENT_NAME = 'Modal';

/**
 * @public
 */
export interface ModalProps
  extends WithChildren,
    BaseModalProps,
    BehaviorTrackingProps {
  /** Title of the modal, which is shown in the header section. */
  title: string | ReactElement;
  /** Footer section of the modal, where customized content can be added. */
  footer?: ReactNode;
}

/** Determines the visual heading level for a modal size */
function getVisualHeadingLevelForSize(
  size: ModalSizeOptionsType,
): 1 | 2 | 3 | 4 | 5 | 6 {
  switch (size) {
    case 'small':
      return 5;
    case 'medium':
    case 'large':
      return 4;
    default:
      return 4;
  }
}

/**
 * The `Modal` component lets you show important, additional content in an overlay.
 * @public
 */
export const Modal = /* @__PURE__ */ forwardRef<HTMLDivElement, ModalProps>(
  (props, forwardedRef) => {
    const {
      show = false,
      title,
      footer,
      size = 'medium',
      children,
      onDismiss,
      dismissible = true,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const visualHeadingSize = getVisualHeadingLevelForSize(size);

    const intl = useIntl();

    const headerId = useId();

    const closeButtonLabel = intl.formatMessage({
      defaultMessage: 'Close modal',
      id: 'aB3uCeB71q6UURBy',
      description: 'Aria-label for close modal button.',
    });

    const hasCustomTitle = !_canReceiveDefaultTextFormat(title);

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    return (
      <BaseModal
        aria-labelledby={headerId}
        show={show}
        size={size}
        onDismiss={onDismiss}
        dismissible={dismissible}
        ref={forwardedRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={consumerClassName}
        style={consumerStyle}
        {...restProps}
        {...behavioralTrackingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <header className={styles.header}>
          {hasCustomTitle ? (
            <div style={{ display: 'contents' }} id={headerId}>
              {title}
            </div>
          ) : (
            <Heading
              id={headerId}
              level={visualHeadingSize}
              as="h2"
              style={{
                wordBreak: 'normal',
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </Heading>
          )}
          {dismissible && (
            <Button
              style={{ alignSelf: 'start' }}
              onClick={onDismiss}
              aria-label={closeButtonLabel}
              variant="default"
              data-dt-name="CloseX"
            >
              <Button.Prefix>
                <XmarkIcon />
              </Button.Prefix>
            </Button>
          )}
        </header>
        {(children || footer) && (
          <div
            className={globalSprinkles({
              paddingBottom: 0,
              paddingTop: 0,
              paddingLeft: 24,
              paddingRight: 24,
            })}
          >
            <Divider />
          </div>
        )}
        {children && (
          <div
            className={globalSprinkles({
              paddingTop: 12,
              paddingRight: 24,
              paddingLeft: 24,
              paddingBottom: footer !== undefined ? 12 : 24,
            })}
            style={{ overflowY: 'auto' }}
          >
            {children}
          </div>
        )}
        {footer && (
          <footer
            className={globalSprinkles({
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              paddingBottom: 24,
              paddingTop: 12,
              paddingLeft: 24,
              paddingRight: 24,
            })}
          >
            {footer}
          </footer>
        )}
      </BaseModal>
    );
  },
);
(Modal as typeof Modal & { displayName: string }).displayName = COMPONENT_NAME;
